import React, { useState } from "react";
import {
  Avatar,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import Person2OutlinedIcon from "@mui/icons-material/Person2Outlined";
import MenuIcon from "@mui/icons-material/Menu";
import InfoIcon from "@mui/icons-material/Info";
import styles from "./header.module.css";
import { useMsal } from "@azure/msal-react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import { clearUser } from "../../store/authSlice";
import ThemeToggleButton from "../ThemeToggleButton";

type HeaderProps = {
  userName?: string;
  isSidebarOpen?: () => void;
  onClickHelp?: () => void;
  onClickLogout?: () => void;
  isShowThemeToggle?: boolean;
};

const Header = ({
  onClickHelp,
  isSidebarOpen,
  isShowThemeToggle,
}: HeaderProps) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const open = Boolean(anchor);
  const mailTo = import.meta.env.VITE_SUPPORT_MAIL || "";
  const feedbackUrl = import.meta.env.VITE_FEEDBACK_FORM_URL;
  const whatsNewUrl = import.meta.env.VITE_WHATS_NEW_URL;

  const dispatch = useDispatch();
  const { instance } = useMsal();

  const userName = useSelector(
    (state: RootState) =>
      state.auth.user?.name || state.auth.user?.username || "Unknown User"
  );

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) =>
    setAnchor(event.currentTarget);
  const handleMenuClose = () => setAnchor(null);

  const onClickLogout = async () => {
    try {
      await instance.logoutPopup({
        postLogoutRedirectUri: "/",
      });
      dispatch(clearUser());
      handleMenuClose();
      window.location.href = "/";
    } catch (err) {
      // Logout error handled by MSAL
    }
  };

  return (
    <div className={styles.header}>
      <IconButton className={styles.menuButton} onClick={isSidebarOpen}>
        <MenuIcon />
      </IconButton>
      <div className={styles.rightSection}>
        <Button
          variant="outlined"
          className={styles.feedbackButton}
          title="Feedback"
          href={feedbackUrl}
          target="_blank"
        >
          <FeedbackOutlinedIcon /> Feedback
        </Button>
        <IconButton
          title="What's New"
          className={styles.infoButton}
          href={whatsNewUrl}
          target="_blank"
        >
          <InfoIcon />
        </IconButton>
        <IconButton
          aria-label="Help"
          className={styles.helpButton}
          title="Help"
          href={`mailto:${mailTo}?subject=Support%20Request`}
        >
          <HelpOutlineOutlinedIcon />
        </IconButton>
        <IconButton
          className={styles.profileIcon}
          aria-controls={open ? "user-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleMenuOpen}
        >
          <Avatar className={styles.avatar}>
            {userName.charAt(0).toUpperCase()}
          </Avatar>
        </IconButton>
        <Menu
          id="user-menu"
          anchorEl={anchor}
          open={open}
          onClose={handleMenuClose}
          classes={{
            root: styles.menuRoot,
            paper: styles.paperRoot,
          }}
        >
          {isShowThemeToggle && (
            <MenuItem className={styles.themeButton}>
              <ThemeToggleButton />
            </MenuItem>
          )}
          <MenuItem>
            <ListItemIcon>
              <Person2OutlinedIcon />
            </ListItemIcon>
            <ListItemText primary={userName} />
          </MenuItem>
          <MenuItem onClick={onClickLogout} className={styles.logoutMenuItem}>
            <ListItemIcon>
              <LogoutOutlinedIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </MenuItem>
        </Menu>
      </div>
    </div>
  );
};

export default Header;
